import { useState } from 'react';
import DOMPurify from 'dompurify';
import { Button } from 'primereact/button';
import { Dialog } from '../dialogs/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useServices } from '../../providers/services-provider';
import { ServiceBookingForm } from './service-booking-form';
import '../../assets/services.css';
import { CatalogueOverview } from '../catalogue-overview';
import { servicesOverview } from '../../content/catalogue-overviews';

export function ServiceDescription({ onBackToList }: { onBackToList?: () => void }) {
    const { selectedKey, service, detailLoading, detailError, reloadDetail } = useServices();
    // A key change discards the previous service's form and draft.
    return <ServiceDescriptionContent key={selectedKey ?? 'none'} service={service?.service_key === selectedKey ? service : null}
        loading={detailLoading} error={detailError} retry={reloadDetail} onBackToList={onBackToList} />;
}

function ServiceDescriptionContent({ service, loading, error, retry, onBackToList }: {
    service: ReturnType<typeof useServices>['service']; loading: boolean; error: string; retry: () => void; onBackToList?: () => void;
}) {
    const [booking, setBooking] = useState(false);
    const [sending, setSending] = useState(false);
    const backButton = <Button label="Back to the list" icon="pi pi-arrow-left" className="mobile-details-back p-button-outlined"
        onClick={onBackToList} />;
    if (loading) return <div className="service-description-pane">
        <div className="service-description-scroll p-4"><ProgressSpinner aria-label="Loading service description" /></div>
        <div className="service-book-bar service-book-bar-mobile-only">{backButton}</div>
    </div>;
    if (error) return <div className="service-description-pane">
        <div className="service-description-scroll p-4" role="alert"><p>{error}</p><Button label="Retry" onClick={retry} /></div>
        <div className="service-book-bar service-book-bar-mobile-only">{backButton}</div>
    </div>;
    if (!service) return <div className="service-description-pane">
        <div className="service-description-scroll catalogue-content p-4"><CatalogueOverview content={servicesOverview} /></div>
        <div className="service-book-bar service-book-bar-mobile-only">{backButton}</div>
    </div>;

    return <div className="service-description-pane">
        <div className="service-description-scroll catalogue-content p-4">
            <h2 className="mt-0">{service.name}</h2>
            <div className="service-description text-color line-height-3" role="region" aria-label={`${service.name} description`}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(service.content, { USE_PROFILES: { html: true } }) }} />
        </div>
        <div className="service-book-bar p-3 border-top-1 surface-border">
            {backButton}
            <Button label="Book" icon="pi pi-calendar" aria-label={`Book ${service.name}`} onClick={() => setBooking(true)} />
        </div>
        <Dialog header={`Book ${service.name}`} visible={booking} modal className="service-booking-dialog"
            style={{ width: '52rem', maxWidth: '96vw' }} breakpoints={{ '640px': '96vw' }}
            closable={!sending} closeOnEscape={!sending} draggable={false}
            onHide={() => { if (!sending) setBooking(false); }}>
            {booking && <ServiceBookingForm service={service} onCancel={() => setBooking(false)} onSendingChange={setSending} />}
        </Dialog>
    </div>;
}
